/**
 * AdminLayout (STORY-008-006). Composes AdminTopBar + AdminSideNav (desktop
 * rail ≥lg; mobile overlay drawer <lg via a hamburger) + <Outlet/>. Wraps the
 * subtree in AdminBreadcrumbProvider (detail pages set the breadcrumb leaf) and
 * installs the global 403→Sonner interceptor. --g-* tokens only.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { AdminSideNav } from "@/features/admin/AdminSideNav";
import { AdminTopBar } from "@/features/admin/AdminTopBar";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminBreadcrumbProvider } from "@/features/admin/components/Breadcrumb";
import { useQueryErrorInterceptor } from "@/lib/queryErrorInterceptor";

export function AdminLayout(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useQueryErrorInterceptor();

  return (
    <AdminBreadcrumbProvider>
      <div
        data-testid="admin-layout"
        className="flex h-screen flex-col overflow-hidden bg-[var(--g-bg)]"
      >
        <AdminTopBar onMenuToggle={() => setMobileNavOpen((v) => !v)} />
        <div className="flex flex-1 overflow-hidden">
          {/* Desktop rail (≥lg) */}
          <div className="hidden lg:flex lg:flex-shrink-0">
            <AdminSideNav />
          </div>
          {/* Mobile overlay drawer (<lg) */}
          {mobileNavOpen ? (
            <div
              data-testid="admin-mobile-nav"
              role="dialog"
              aria-modal="true"
              aria-label={t("nav.ariaLabel")}
              className="fixed inset-0 z-40 lg:hidden"
            >
              <button
                type="button"
                aria-label={t("nav.openMenu")}
                className="absolute inset-0 bg-black/40"
                onClick={() => setMobileNavOpen(false)}
              />
              <div className="absolute left-0 top-0 h-full">
                <AdminSideNav onNavigate={() => setMobileNavOpen(false)} />
              </div>
            </div>
          ) : null}
          <main
            data-testid="admin-main-content"
            className="flex-1 overflow-y-auto p-4 sm:p-6"
          >
            <Outlet />
          </main>
        </div>
      </div>
    </AdminBreadcrumbProvider>
  );
}

export default AdminLayout;
